import { useEffect, useState, useMemo } from "react";
import debounce from "lodash/debounce";
import { useSelector, useDispatch } from "react-redux";
import { Form, Formik } from "formik";
import styles from "./styles.module.sass";
import { Button } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { AiFillCloseCircle } from "react-icons/ai";
import {
  fetchPropertyByRealId,
  editPropertyAgent,
  addPropertyAgent,
  checkPropertyCodeAvailability,
  addTaskAgent,
} from "../../state/actions/propertyActions";
import { AdminSchema } from "../../validation/propertyValidation";
import { getAllAgents } from "../../state/actions/agentActions";
import { getAllUsers } from "../../state/actions/accountActions";
import { formatLocalDateTime } from "../../utils/dateTime";
import {
  Recommend,
  PropertyBasicsSection,
  PropertyPricingSection,
  PropertyFeaturesSection,
  PropertyAdminSection,
  PropertyDescriptionSection,
} from "../../components/PropertyForm";
import {
  FLOOR_OPTIONS,
  PROPERTY_CATEGORIES,
  generateYears,
} from "../../utils/propertyConstants";
import {
  mapPropertyFormValuesToSubmitValues,
  buildPropertyInitialValues,
} from "../../utils/propertyFormMapper";
import { usePropertyCheckboxState } from "../../hooks/usePropertyCheckboxState";
import {
  buildTypeAndSubTypeOptions,
  getRoomNumberOptions,
  getFacilitiesOptions,
  getFloorsOptions,
  getHeatingModeOptions,
  getAccompanyingOptions,
  getHeatingModeTypeOptions,
  getLotTechnicalOptions,
  getBathroomsOptions,
  getAssistOptions,
} from "../../utils/propertyFormOptions";

const AddProperty = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const lang = "sr";

  // Selectors
  const currentProperty = useSelector(
    (state) => state.property.currentProperty
  );

  const filters = useSelector((state) => state.staticValues.filters);
  const translations = useSelector((state) => state.staticValues.translations);
  const cities = useSelector((state) => state.staticValues.cities);
  const user = useSelector((state) => state.account.user?.id);
  const loggedUserAgencyType = useSelector(
    (state) =>
      state.account.user?.agencies?.[0]?.type ??
      state.account.user?.agentInfo?.agencies?.[0]?.type
  );
  const isAgent = useSelector(
    (state) =>
      state.account.user?.role === "agent" ||
      state.account.user?.role === "menadzer" ||
      state.account.user?.role === "rukovodilac"
  );
  const differences = useSelector((state) => state.property.differences);
  const differencesMapped = useSelector(
    (state) => state.property.differencesMapped
  ) || { options: {} };
  const availablePropertyCode = useSelector(
    (state) => state.property.isReservedCode
  );
  const tasks = useSelector((state) => state.property.tasks) || [];
  const recommendations =
    useSelector((state) => state.property.recommendations) || [];
  const recommends = useMemo(
    () =>
      recommendations
        .filter((r) => !r.action_taken && !r.actionTaken)
        .map((r) => {
          // Stored as `{ [key]: boolValue }` (e.g. { lux: true }).
          // Take the first key — that is the recommendation id.
          if (r.data && typeof r.data === "object") {
            const keys = Object.keys(r.data);
            if (keys.length > 0) return keys[0];
          }
          return r.data?.key;
        })
        .filter(Boolean),
    [recommendations]
  );

  const agentsList = useSelector((state) => state.admin.agents) || [];
  const agents = useMemo(
    () => [...agentsList].sort((a, b) => (a.name < b.name ? -1 : 1)),
    [agentsList]
  );
  const {
    filtersSelect,
    supportFiltersSelect,
    balconyFiltersSelect,
    checkboxesDifference,
    handleCheckboxClick,
    handleSupportCheckboxClick,
    handleBaclconyCheckboxClick,
    initializeCheckboxState,
  } = usePropertyCheckboxState();
  const router = window.location.pathname;
  const [difference, setDifferences] = useState([]);

  const loggedAgentData = agents.find((item) => item._id === user);
  const roominessInitialValue = (selectedType) => {
    if (selectedType === "menu_houses") {
      return false;
    } else if (selectedType === "top_search_cat_menu_apartments") {
      return true;
    }
  };

  const getPropertyCategories = () => PROPERTY_CATEGORIES;

  const submitForm = (values) => {
    const submitValues = mapPropertyFormValuesToSubmitValues({
      values,
      filtersSelect,
      supportFiltersSelect,
      balconyFiltersSelect,
    });
    if (params.id) {
      dispatch(editPropertyAgent(submitValues, currentProperty.id)).then(() =>
        navigate("/home")
      );
    } else {
      dispatch(addPropertyAgent(submitValues)).then(() => navigate("/home"));
    }
  };

  useEffect(() => {
    setDifferences(Object.keys(differences).map((diff) => diff));
  }, [differences.length]);

  const checkCodeAvailability = useMemo(
    () =>
      debounce((value) => {
        if (value) {
          dispatch(checkPropertyCodeAvailability(value));
        }
      }, 500),
    [dispatch]
  );

  const { filt, subFilters } = buildTypeAndSubTypeOptions({
    filters,
    translations,
    lang,
  });

  const getRoomNumbers = getRoomNumberOptions(filters);

  const propertyState = getFacilitiesOptions({
    moreFilters: filters,
    translations,
    lang,
    name: "form_input_property_status",
  });

  const getFloors = () =>
    getFloorsOptions({ translations, lang, floorOptions: FLOOR_OPTIONS });

  const heatingMode = () =>
    getHeatingModeOptions({ moreFilters: filters, translations, lang });

  const accompaning = () =>
    getAccompanyingOptions({ moreFilters: filters, translations, lang });

  const heatingModeType = () =>
    getHeatingModeTypeOptions({ moreFilters: filters, translations, lang });

  const lotTechnical = () =>
    getLotTechnicalOptions({ moreFilters: filters, translations, lang });

  const getBathrooms = () => getBathroomsOptions({ moreFilters: filters });

  const getAssist = () =>
    getAssistOptions({ moreFilters: filters, translations, lang });

  const technical = getFacilitiesOptions({
    moreFilters: filters,
    translations,
    lang,
    name: "form_input_property_technical_equipment",
  });

  useEffect(() => {
    dispatch(getAllUsers());
    dispatch(getAllAgents());
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET_CURRENT_PROPERTY" });

    if (params.id) {
      dispatch(
        fetchPropertyByRealId(params.id, isAgent ? user : undefined, true)
      ).then((res) => {
        const property = res.data.data;
        const diffMapped = res.data.differencesMapped;
        initializeCheckboxState(property, diffMapped);
      });
    }

    return () => {
      dispatch({ type: "RESET_CURRENT_PROPERTY" });
    };
  }, [params.id, initializeCheckboxState]);

  const findType = (type) => {
    const found = filters?.find((f) => f.type_name === type);
    return found?.type_key || type;
  };

  if (Object.keys(cities).length === 0) {
    return null;
  }

  return (
    <div key={`property-form-container`} className={styles.editProperty}>
      <div key={`property-form-subcontainer`} className={styles.subcontainer}>
        <h1 style={{ maxWidth: "unset" }}>
          {params.id ? "Izmenite nekretninu" : "Dodajte nekretninu"}
        </h1>
        <Formik
          key={`formik-property-form-${currentProperty?.id ?? "new"}-${loggedUserAgencyType ?? "notype"}`}
          onSubmit={submitForm}
          validationSchema={AdminSchema}
          initialValues={buildPropertyInitialValues({
            isEditMode: !!params.id,
            isAgent,
            currentProperty,
            differencesMapped,
            loggedAgentData,
            loggedUserAgencyType,
            findType,
          })}
        >
          {({ handleSubmit, handleChange, values, touched, errors }) => (
            <Form key={`property-form`} className="mb-5">
              <PropertyBasicsSection
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                params={params}
                availablePropertyCode={availablePropertyCode}
                styles={styles}
                difference={difference}
                router={router}
                currentProperty={currentProperty}
                checkCodeAvailability={checkCodeAvailability}
                isAgent={isAgent}
                getPropertyCategories={getPropertyCategories}
                loggedAgentData={loggedAgentData}
                loggedUserAgencyType={loggedUserAgencyType}
                agents={agents}
                filt={filt}
                subFilters={subFilters}
                roominessInitialValue={roominessInitialValue}
                cities={cities}
              />
              <PropertyPricingSection
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                styles={styles}
                difference={difference}
                router={router}
                generateYears={generateYears}
                getRoomNumbers={getRoomNumbers}
                propertyState={propertyState}
                getFloors={getFloors}
                getBathrooms={getBathrooms}
              />
              <PropertyFeaturesSection
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                styles={styles}
                difference={difference}
                router={router}
                accompaning={accompaning}
                getAssist={getAssist}
                lotTechnical={lotTechnical}
                heatingMode={heatingMode}
                heatingModeType={heatingModeType}
                technical={technical}
                checkboxesDifference={checkboxesDifference}
                filtersSelect={filtersSelect}
                supportFiltersSelect={supportFiltersSelect}
                balconyFiltersSelect={balconyFiltersSelect}
                handleCheckboxClick={handleCheckboxClick}
                handleSupportCheckboxClick={handleSupportCheckboxClick}
                handleBaclconyCheckboxClick={handleBaclconyCheckboxClick}
              />
              <div
                key={`property-form-checkboxes-section`}
                style={{
                  height: 1,
                  width: "100%",
                  background: "#ddd",
                  margin: "10px 0",
                }}
              ></div>
              <PropertyAdminSection
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                styles={styles}
                difference={difference}
                router={router}
                isAgent={isAgent}
                currentProperty={currentProperty}
              />
              <PropertyDescriptionSection
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                styles={styles}
                difference={difference}
                router={router}
                isAgent={isAgent}
                differencesMapped={differencesMapped}
              />
              <Button
                key={`form-button-save`}
                className="btn btn-xs mt-2"
                onClick={handleSubmit}
                disabled={
                  !params.id && values.property_code && availablePropertyCode
                    ? true
                    : false
                }
              >
                {params.id ? "Sačuvaj izmene" : "Dodaj nekretninu"}
              </Button>
            </Form>
          )}
        </Formik>
        {isAgent && currentProperty.property_code && (
          <Recommend
            key={`recommend-commponent`}
            type={currentProperty.type}
            agentId={currentProperty.agent_id}
            propertyId={currentProperty.property_code}
            codeId={currentProperty.id}
            recommends={recommends}
            styles={styles}
            fields={{
              lux: currentProperty.lux,
              top: currentProperty.top,
              cheap: currentProperty.cheap,
              new: currentProperty.new,
              urgent: currentProperty.urgent,
              top_studio: currentProperty.top_studio,
              exclusive: currentProperty.exclusive,
            }}
          />
        )}
        {currentProperty.property_code && (
          <div key={`property-form-tasks-container`} style={{ marginTop: 50 }}>
            <h3>Zadaci od administratora:</h3>
            {tasks.map((task, index) => (
              <div
                key={`property-form-tasks-agent-box-${index}`}
                className={styles.agentBox}
              >
                <div key={`property-form-single-box`}>
                  [{formatLocalDateTime(task.created_at)}]: Administrator{" "}
                  <strong>{task.sender_info?.username}</strong> dao je zadatak:{" "}
                  <strong>{task.message}</strong>
                </div>
                {!task.action_taken && isAgent ? (
                  <button
                    type="button"
                    className={styles.circularButtonFalse}
                    onClick={() => {
                      dispatch(addTaskAgent(task.id)).then(() => {
                        dispatch(fetchPropertyByRealId(params.id));
                      });
                    }}
                  >
                    <AiFillCloseCircle />
                  </button>
                ) : (
                  <div>Obavljeno.</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddProperty;
